import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { MultiSelect } from "primereact/multiselect";
import TabsNavbar from "../../Common/TabsNavBar/TabsNavbar";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import Select from "../../Common/FormElements/Select/Select";
import Button from "../../Common/FormElements/Button/Button";
import Location from "../../Prospects/NewProspect/Location/Location";
import CompetitorComments from "./CompetitorComments";
import API from "../../../Api/Api";
import { API_END_POINT } from "../../../Api/EndPoints";
import { closeRightModal } from "../../../REDUX/actions/rightModal";
import { createUpdateCompetitor } from "../../../REDUX/actions/competitors";
import { competitorForm } from "./data";
import { usePartnerOrganizations } from "../usePartnerOrganizations";

// Tab labels are raw i18n keys — TabsNavbar translates internally.
const TABS = ["Informations", "Location", "Comments"];

/** Select value from a stored code: matching option, else a synthetic one. */
const toOption = (value, options = []) =>
  value
    ? options.find((o) => o.code === value) ?? { name: value, code: value }
    : null;

const CompetitorModal = ({ competitor }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("common");
  const isEdit = !!competitor;
  const attributes = competitor?.attributes ?? {};

  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [selectOptions, setSelectOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);

  // Admin: any store (optional); business affiliate: one it sponsors
  // (required); reseller: pinned server-side to its own store.
  const { isAffiliate, canPickOrganization, organizationOptions } =
    usePartnerOrganizations();
  const { scope } = useSelector((state) => state.appCustomersReducer);

  const [formData, setFormData] = useState(() => {
    const initial = {};
    competitorForm.forEach((el) => {
      if (el.type === "multiSelect") {
        initial[el.name] =
          attributes[el.name]?.data?.map((item) => item.id) ?? [];
      } else if (el.type === "select") {
        initial[el.name] = toOption(attributes[el.name], el.options);
      } else {
        initial[el.name] = attributes[el.name] ?? "";
      }
    });
    initial.markers = attributes.markers ?? [];
    const organizationId =
      attributes.organization?.data?.id ?? (isEdit ? null : scope.organizationId);
    initial.organization = organizationId ? { code: organizationId } : null;
    return initial;
  });

  // Select matches its value by equality — use the loaded option object.
  useEffect(() => {
    if (!formData.organization?.code || !organizationOptions.length) return;
    const match = organizationOptions.find(
      (o) => o.code === formData.organization.code
    );
    if (match && match !== formData.organization) {
      setFormData((prev) => ({ ...prev, organization: match }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationOptions]);

  // Same option sourcing as the prospect Informations tab (no
  // business-affiliates call — the Account Manager field is not on this form).
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const [activityData, spaMarqueData] = await axios.all([
          API.get(`${API_END_POINT}api/activities`, {
            signal: controller.signal,
          }),
          API.get(`${API_END_POINT}api/spa-marques`, {
            signal: controller.signal,
          }),
        ]);
        setSelectOptions({
          activities: activityData?.data?.data?.map((activity) => ({
            label: i18n.language?.includes("en")
              ? activity.attributes?.name_en
              : activity.attributes?.name,
            value: activity?.id,
          })),
          spaMarques: spaMarqueData?.data?.data?.map((marque) => ({
            label: marque.attributes?.name,
            value: marque?.id,
          })),
        });
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      }
    })();
    return () => controller.abort();
  }, [i18n.language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const checkFormValidation = useCallback(() => {
    const invalidFields = [];
    competitorForm.forEach(({ name, required, type }) => {
      if (!required) return;
      if (type === "select") {
        if (!formData[name]?.code) invalidFields.push(name);
      } else if (!String(formData[name] ?? "").trim()) {
        invalidFields.push(name);
      }
    });
    if (isAffiliate && !formData.organization?.code) {
      invalidFields.push("organization");
    }
    setDisabled(invalidFields.length > 0);
  }, [formData, isAffiliate]);

  useEffect(() => {
    checkFormValidation();
  }, [checkFormValidation]);

  const handleSave = () => {
    setLoading(true);
    dispatch(
      createUpdateCompetitor(formData, competitor?.id, t, () => {
        setLoading(false);
        dispatch(closeRightModal());
      })
    );
  };

  return (
    <div className="p-4 d-flex flex-column h-100">
      <h2 className="rightModalTitle quickSand bold text-blue mb-4">
        {isEdit ? t("Modify a competitor") : t("Add a competitor")}
      </h2>
      <TabsNavbar
        tabs={TABS}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        // Comments need an existing record.
        disabledIndexes={isEdit ? [] : [2]}
      />
      {activeTab === "Informations" ? (
        <div className="row">
          {competitorForm.map((el) => (
            <div className="col-6 mt-4" key={el?.name}>
              <p className="f16 text-ultraBlack mb-2">
                {t(`${el?.label}`)}
                {el?.required ? <span className="text-red"> *</span> : null}
              </p>
              {el.type === "select" ? (
                <Select
                  {...el}
                  label={el.label ? t(`${el.label}`) : ""}
                  placeholder={el.placeholder ? t(`${el.placeholder}`) : ""}
                  value={formData[el?.name]}
                  options={selectOptions[el?.name] ?? el.options ?? []}
                  callback={handleChange}
                  appendToSelf
                />
              ) : el.type === "multiSelect" ? (
                <MultiSelect
                  name={el?.name}
                  value={formData[el?.name]}
                  options={selectOptions[el?.name] ?? []}
                  placeholder={el.placeholder ? t(`${el.placeholder}`) : ""}
                  onChange={handleChange}
                  className="h-40px w-100"
                />
              ) : (
                <TextInput
                  {...el}
                  value={formData[el?.name]}
                  callback={handleChange}
                />
              )}
            </div>
          ))}
          {canPickOrganization ? (
            <div className="col-6 mt-4">
              <p className="f16 text-ultraBlack mb-2">
                {t("Reseller")}
                {isAffiliate ? <span className="text-red"> *</span> : null}
              </p>
              <Select
                name="organization"
                value={formData.organization}
                options={organizationOptions}
                callback={handleChange}
                filter
                showClear
                appendToSelf
              />
            </div>
          ) : null}
        </div>
      ) : activeTab === "Location" ? (
        // Single point, like app customers — a competitor is one shop.
        // 650px: the shared .map class has a 600px min-height + own padding.
        <div style={{ height: 650 }} className="mt-3">
          <Location
            singleMarker
            initialValues={{ markers: formData.markers }}
            handleFormChange={(_, data) =>
              setFormData((prev) => ({ ...prev, markers: data?.markers ?? [] }))
            }
          />
        </div>
      ) : (
        <CompetitorComments competitorId={competitor?.id} />
      )}
      {activeTab !== "Comments" && (
        <div className="d-flex justify-content-end mt-5">
          <Button
            variant="outline"
            className="me-4"
            onClick={() => dispatch(closeRightModal())}
          >
            {t("Cancel")}
          </Button>
          <Button
            loading={loading}
            disabled={disabled}
            onClick={loading ? undefined : handleSave}
          >
            {t("Save")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompetitorModal;
